import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { FadeUp } from "../components/Motion";
import { ArrowLeft, MapPin } from "lucide-react";
import SEO from "../components/SEO";
import { PROJECT_OVERRIDES, PROJECT_SLUG_ALIASES } from "../lib/projectOverrides";

const CAT_LABEL = {
  "custom-home": "Custom Home",
  "renovation": "Renovation",
  "commercial": "Commercial",
  "federal": "Federal",
  "disaster": "Disaster Recovery",
  "public-infrastructure": "Public Infrastructure",
  "federal-construction": "Federal Construction",
  "federal-consulting": "Federal Consulting",
  "construction-claims": "Construction Claims",
};

function applyOverride(doc, slug) {
  const over = PROJECT_OVERRIDES[slug];
  if (!over) return doc;
  // Shallow merge; override wins. Explicit nulls in override are kept.
  return { ...doc, ...over };
}

export default function ProjectDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [showBefore, setShowBefore] = useState(false);

  useEffect(() => {
    // Alias redirect handled via early-return in render; just skip fetch here.
    if (PROJECT_SLUG_ALIASES[slug]) return;
    setLoading(true);
    api.get(`/projects/${slug}`)
      .then((r) => setP(applyOverride(r.data, slug)))
      .catch(() => {
        if (PROJECT_OVERRIDES[slug]) {
          setP(applyOverride({ slug, category: "federal" }, slug));
        } else {
          setP(null);
        }
      })
      .finally(() => setLoading(false));
  }, [slug]);

  // Alias redirect for legacy slugs, after hooks are declared
  if (PROJECT_SLUG_ALIASES[slug]) {
    return <Navigate to={`/projects/${PROJECT_SLUG_ALIASES[slug]}`} replace />;
  }

  if (loading) return <div className="min-h-screen bg-cream-light flex items-center justify-center"><span className="overline">Loading…</span></div>;
  if (!p) return (
    <>
      <Nav />
      <div className="pt-40 pb-40 text-center bg-cream-light">
        <h1 className="font-serif text-4xl text-ink mb-6">Project not found</h1>
        <Link to="/projects" className="btn-copper">View Our Work →</Link>
      </div>
      <Footer />
    </>
  );

  const hasBefore = p.before_images && p.before_images.length > 0;
  const gallery = showBefore && hasBefore ? p.before_images : (p.gallery || (p.image_url ? [p.image_url] : []));
  const heroImg = gallery[activeImg] || p.image_url;

  return (
    <>
      <SEO title={p.title} description={p.description || p.title} path={`/projects/${slug}`} image={p.image_url} type="article" />
      <Nav transparentOnTop />

      {/* Hero */}
      <section data-testid={`project-hero-${slug}`} className="relative min-h-[70vh] bg-ink overflow-hidden">
        {heroImg && (
          <>
            <img src={heroImg} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30" />
          </>
        )}
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 pt-32 pb-14 min-h-[70vh] flex flex-col justify-end">
          <Link to="/projects" className="text-[11px] tracking-[0.18em] uppercase text-copper-light flex items-center gap-2 mb-6 hover:text-cream-light w-fit">
            <ArrowLeft size={14} /> Back to Our Work
          </Link>
          <div className="text-[11px] tracking-[0.18em] uppercase text-copper-light mb-4">{p.__category_label || CAT_LABEL[p.category] || p.category}</div>
          <h1 className="font-serif font-medium text-cream-light tracking-tight mb-4 leading-[1.05]" style={{ fontSize: "clamp(32px, 5vw, 56px)" }}>{p.title}</h1>
          {p.address && (
            <div className="text-cream-light/80 flex items-center gap-2">
              <MapPin size={16} className="text-copper-light" /> {p.address}
            </div>
          )}
        </div>
      </section>

      {/* Narrative */}
      <section className="bg-cream-light py-16 md:py-20">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          {p.description && (
            <p className="font-serif text-ink leading-snug mb-8" style={{ fontSize: "clamp(22px, 2.4vw, 32px)" }}>{p.description}</p>
          )}
          {p.long_description && (
            <p className="text-ink/80 leading-relaxed" style={{ fontSize: 17 }}>{p.long_description}</p>
          )}
        </div>
      </section>

      {/* Key Features */}
      {p.key_features?.length > 0 && (
        <section className="bg-cream py-16 md:py-20">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <div className="text-[11px] tracking-[0.18em] uppercase text-copper mb-5">Key Features</div>
            <h2 className="font-serif font-medium text-ink tracking-tight mb-10" style={{ fontSize: "clamp(26px, 3vw, 36px)" }}>The details that make it.</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-4">
              {p.key_features.map((f, i) => (
                <FadeUp key={f} delay={i * 40}>
                  <div className="flex items-start gap-3 pb-4 border-b border-ink/10">
                    <span className="font-serif text-copper text-xl leading-none mt-1">{String(i+1).padStart(2,'0')}</span>
                    <span className="text-ink/85">{f}</span>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="bg-cream-light py-16 md:py-20">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <div className="text-[11px] tracking-[0.18em] uppercase text-copper mb-3">Gallery</div>
                <h2 className="font-serif font-medium text-ink tracking-tight" style={{ fontSize: "clamp(26px, 3vw, 36px)" }}>
                  {hasBefore ? (showBefore ? "Before." : "After.") : "The finished work."}
                </h2>
              </div>
              {hasBefore && (
                <div className="inline-flex border border-ink/20 p-1" data-testid="before-after-toggle">
                  <button onClick={() => { setShowBefore(false); setActiveImg(0); }} className={`px-5 py-2 text-[11px] tracking-[0.18em] uppercase ${!showBefore ? "bg-ink text-cream-light" : "text-ink"}`}>After</button>
                  <button onClick={() => { setShowBefore(true); setActiveImg(0); }} className={`px-5 py-2 text-[11px] tracking-[0.18em] uppercase ${showBefore ? "bg-ink text-cream-light" : "text-ink"}`}>Before</button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
              {gallery.map((img, i) => {
                const span = i === 0 ? "md:col-span-12 md:row-span-2 md:h-[600px]" : i % 3 === 0 ? "md:col-span-8 md:h-[420px]" : "md:col-span-4 md:h-[420px]";
                return (
                  <FadeUp key={img+i} delay={Math.min(i * 60, 400)} className={span}>
                    <button onClick={() => setActiveImg(i)} className="block w-full h-full group overflow-hidden bg-ink">
                      <img src={img} alt={`${p.title}, image ${i+1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                    </button>
                  </FadeUp>
                );
              })}
            </div>
            {p.photo_credit && (
              <div className="mt-6 text-sm text-ink/60 italic">{p.photo_credit}</div>
            )}
          </div>
        </section>
      )}

      {/* Testimonial */}
      {p.testimonial_quote && (
        <section data-testid="project-testimonial" className="bg-cream py-16 md:py-20">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10">
            <div className="text-[11px] tracking-[0.18em] uppercase text-copper mb-6">In the owner's words</div>
            <FadeUp>
              <blockquote className="border-l-2 border-copper pl-6 md:pl-8">
                <p className="font-serif text-ink leading-snug mb-6" style={{ fontSize: "clamp(20px, 2.4vw, 30px)" }}>"{p.testimonial_quote}"</p>
                <footer className="flex items-center gap-4">
                  <span className="w-10 h-px bg-copper" />
                  <div>
                    {p.testimonial_author && <div className="font-serif text-lg text-ink">{p.testimonial_author}</div>}
                    {p.testimonial_role && <div className="text-[11px] tracking-[0.16em] uppercase text-ink/55 mt-1">{p.testimonial_role}</div>}
                  </div>
                </footer>
              </blockquote>
            </FadeUp>
          </div>
        </section>
      )}

      <section className="bg-ink text-cream-light py-20 text-center">
        <div className="max-w-[800px] mx-auto px-6">
          <h2 className="font-serif font-medium mb-8 tracking-tight" style={{ fontSize: "clamp(26px, 3vw, 36px)" }}>Have a similar project in mind?</h2>
          <Link to="/contact" className="btn-copper">Start a Project →</Link>
        </div>
      </section>

      <Footer />
    </>
  );
}
